import { useState, useEffect } from 'react';
import { useSelector } from 'react-redux';
import firebase from '../config/firebase.config';
import _ from 'lodash';

const database = firebase.firestore();

/**
 * Custom hook to fetch and cache stealthMode setting from companyDatabase
 * Follows DRY principle - fetches once, caches in localStorage, updates on company change
 *
 * @param {string} [overrideCompanyId] - When set (non-empty), read stealth for this company instead of the user's active company (e.g. Employee List company dropdown).
 * @returns {{ stealthMode: boolean, loading: boolean }} stealthMode - true if enabled, false if disabled or not set
 */
export function useStealthMode(overrideCompanyId) {
  const userProfile = useSelector((state) => state.auth.profile);
  const activeCompanyId = !_.isEmpty(userProfile?.activeCompany)
    ? userProfile?.activeCompany?.companyId
    : null;
  const companyId =
    typeof overrideCompanyId === 'string' && overrideCompanyId.trim() !== ''
      ? overrideCompanyId.trim()
      : activeCompanyId;

  const [stealthMode, setStealthMode] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!companyId) {
      setStealthMode(false);
      setLoading(false);
      return;
    }

    // Check localStorage cache first
    const cacheKey = `stealthMode_${companyId}`;
    const cachedValue = localStorage.getItem(cacheKey);

    if (cachedValue !== null) {
      // Use cached value immediately
      setStealthMode(cachedValue === 'true');
      setLoading(false);
    }

    // Fetch from Firestore (will update if different from cache)
    const companyRef = database.collection('companyDatabase').doc(companyId);

    const unsubscribe = companyRef.onSnapshot(
      (doc) => {
        if (doc.exists) {
          const data = doc.data();
          // Default to false if field doesn't exist
          const newStealthMode = data.stealthMode === true;
          setStealthMode(newStealthMode);
          // Update cache
          localStorage.setItem(cacheKey, String(newStealthMode));
        } else {
          // Document doesn't exist, default to false
          setStealthMode(false);
          localStorage.setItem(cacheKey, 'false');
        }
        setLoading(false);
      },
      (error) => {
        console.error('Error fetching stealthMode:', error);
        // On error, use cached value if available, otherwise default to false
        if (cachedValue !== null) {
          setStealthMode(cachedValue === 'true');
        } else {
          setStealthMode(false);
        }
        setLoading(false);
      }
    );

    // Cleanup: clear cache when company changes
    return () => {
      unsubscribe();
    };
  }, [companyId]);

  // Clear cache when company changes
  useEffect(() => {
    if (companyId) {
      // Cache will be updated by the snapshot listener above
      // This effect ensures we're ready for company changes
      const cacheKey = `stealthMode_${companyId}`;
      // Don't clear here - let the snapshot listener update it
      // But we can clear old caches if needed
      const allKeys = Object.keys(localStorage);
      allKeys.forEach((key) => {
        if (key.startsWith('stealthMode_') && key !== cacheKey) {
          // Optionally clear old company caches to save space
          // localStorage.removeItem(key);
        }
      });
    }
  }, [companyId]);

  return { stealthMode, loading };
}
